import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { apiFetch } from '../../../utils';
import { useModal } from '../modal';

import './index.css';

const BountiesModal = () => {
    const { closeModal } = useModal();
    const user = useSelector((state) => state?.session?.user);

    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [results, setResults] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    const [payerName, setPayerName] = useState('');
    const [targetName, setTargetName] = useState('');
    const [amount, setAmount] = useState('1000');
    const [posting, setPosting] = useState(false);
    const [postErrors, setPostErrors] = useState([]);
    const [postSuccess, setPostSuccess] = useState('');
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let isCancelled = false;

        const loadBounties = async () => {
            setLoading(true);
            setErrors([]);

            const response = await apiFetch('/user/bounties', 'POST', {
                page,
                pageSize,
            });

            if (isCancelled) return;

            if (!response || response.code !== 'success') {
                const err = response?.data?.msg || ['Failed to load bounties.'];
                setErrors(Array.isArray(err) ? err : ['Failed to load bounties.']);
                setResults([]);
                setTotalPages(1);
                setTotal(0);
                setLoading(false);
                return;
            }

            const data = response.data || {};
            setResults(Array.isArray(data.results) ? data.results : []);
            setTotalPages(Math.max(data.total_pages || 1, 1));
            setTotal(data.total || 0);
            setLoading(false);
        };

        loadBounties();

        return () => {
            isCancelled = true;
        };
    }, [page, pageSize, reloadKey]);

    const submitBounty = async (event) => {
        event.preventDefault();

        setPostErrors([]);
        setPostSuccess('');

        if (!user) {
            setPostErrors(['You must be logged in to post a bounty.']);
            return;
        }

        const parsedAmount = Number.parseInt(amount, 10);
        if (!Number.isInteger(parsedAmount) || parsedAmount < 1000) {
            setPostErrors(['Bounty amount must be at least 1000 gold.']);
            return;
        }

        setPosting(true);

        const response = await apiFetch('/user/bounties/create', 'POST', {
            payerName,
            targetName,
            amount: parsedAmount,
        });

        if (!response || response.code !== 'success') {
            const err = response?.data?.msg || ['Failed to post bounty.'];
            setPostErrors(Array.isArray(err) ? err : ['Failed to post bounty.']);
            setPosting(false);
            return;
        }

        const addedAmount = Number(response?.data?.amount_added || parsedAmount).toLocaleString();
        const postedTarget = response?.data?.bounty || targetName;
        setPostSuccess(`Posted ${addedAmount} gold on ${postedTarget}.`);
        setTargetName('');
        setAmount('1000');
        setPosting(false);
        setPage(1);
        setReloadKey((value) => value + 1);
    };

    return (
        <div id='bounties-modal'>
            <div id='bounties-modal-shell'>
                <div className='modal-panel-header'>
                    <h2 className='modal-panel-title'>Bounties</h2>
                    <button
                        type='button'
                        className='button modal-panel-close'
                        title='Close bounties'
                        onClick={closeModal}
                    >
                        Close
                    </button>
                </div>

                <form id='bounties-create-form' onSubmit={submitBounty}>
                    <h3 id='bounties-create-title'>Post Bounty</h3>
                    <p id='bounties-create-subtitle'>Use one of your characters to place a bounty. Minimum: 1,000 gold.</p>

                    {postErrors.map((error) => <p className='modal-error-message' key={`post-${error}`}>{error}</p>)}
                    {postSuccess && <p id='bounties-post-success'>{postSuccess}</p>}

                    <div id='bounties-create-grid'>
                        <label className='bounties-create-field'>
                            <span>Your Character</span>
                            <input
                                className='bounties-input'
                                type='text'
                                maxLength='20'
                                value={payerName}
                                onChange={(event) => setPayerName(event.target.value)}
                                placeholder='Your character name'
                                disabled={posting || !user}
                                required
                            />
                        </label>

                        <label className='bounties-create-field'>
                            <span>Bounty Target</span>
                            <input
                                className='bounties-input'
                                type='text'
                                maxLength='20'
                                value={targetName}
                                onChange={(event) => setTargetName(event.target.value)}
                                placeholder='Target character name'
                                disabled={posting || !user}
                                required
                            />
                        </label>

                        <label className='bounties-create-field'>
                            <span>Amount</span>
                            <input
                                className='bounties-input'
                                type='number'
                                min='1000'
                                step='1'
                                value={amount}
                                onChange={(event) => setAmount(event.target.value)}
                                placeholder='1000'
                                disabled={posting || !user}
                                required
                            />
                        </label>

                        <button
                            type='submit'
                            className='button'
                            id='bounties-submit-button'
                            disabled={posting || !user}
                        >
                            {posting ? 'Posting...' : 'Post Bounty'}
                        </button>
                    </div>

                    {!user && <p id='bounties-login-hint'>Log in to post a bounty.</p>}
                </form>

                <div id='bounties-table-wrap'>
                    {errors.map((error) => <p className='modal-error-message' key={error}>{error}</p>)}

                    {!errors.length && (
                        <table id='bounties-table'>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Bounty</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan='3' className='bounties-empty'>Loading bounties...</td>
                                    </tr>
                                )}

                                {!loading && results.length === 0 && (
                                    <tr>
                                        <td colSpan='3' className='bounties-empty'>No bounties found.</td>
                                    </tr>
                                )}

                                {results.map((row, index) => (
                                    <tr key={`${row.id}-${index}`}>
                                        <td>{((page - 1) * pageSize) + index + 1}</td>
                                        <td>{row.bounty}</td>
                                        <td>{Number(row.amount || 0).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div id='bounties-pagination'>
                    <button
                        type='button'
                        className='button bounties-page-button'
                        disabled={loading || page <= 1}
                        onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    >
                        Previous
                    </button>

                    <p>
                        Page {page} / {totalPages} ({total.toLocaleString()} total)
                    </p>

                    <button
                        type='button'
                        className='button bounties-page-button'
                        disabled={loading || page >= totalPages}
                        onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BountiesModal;
